import {
  Component,
  OnInit,
  HostListener,
  ChangeDetectorRef,
} from "@angular/core";
import {
  Router,
  NavigationEnd,
  RouteConfigLoadStart,
  RouteConfigLoadEnd,
  ResolveStart,
  ResolveEnd,
} from "@angular/router";
import { Subscription } from "rxjs";
import { TranslateService } from "@ngx-translate/core";
import { ThemeService } from "../../../services/theme.service";
import { AdminContainerClasses, ILayoutConf, LayoutService } from "../../../services/layout.service";
import { filter } from "rxjs/operators";
import { JwtAuthService } from "../../../services/auth/jwt-auth.service";
import { CookieService } from "ngx-cookie-service";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { KycStatusService, KycStatusSnapshot } from "../../../services/kyc-status.service";
import { AuthSessionStateService } from "../../../services/auth-session-state.service";
import { ProfileSetupPromptComponent } from "../../../../views/dashboard/supplier/profile-setup-prompt/profile-setup-prompt.component";

@Component({
  selector: "app-admin-layout",
  templateUrl: "./admin-layout.component.html",
  // changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminLayoutComponent implements OnInit {
  public isModuleLoading: Boolean = false;
  public suppressViewLoader: boolean = false;
  private moduleLoaderSub!: Subscription;
  private layoutConfSub!: Subscription;
  private routerEventSub: Subscription;
  // private routerEvent: Subscription;

  public scrollConfig: any = {};
  public layoutConf: ILayoutConf = {};
  public adminContainerClasses: AdminContainerClasses | {} = {};

  isWarningNotification: boolean = false;
  isForceNotification: boolean = false;
  notificationDays: number = 0;

  showBackdrop: boolean = false;

  //dashboardSideNavReturnUrl: string = "/dashboard/owner";
  dashboardSideNavReturnUrl: string = "/environmental/emissions/tonnesofquantityconsumed";

  shouldCollapseSidebar: boolean = false;
  hideChrome: boolean = false;
  // One-shot flag so the KYC modal only opens on the first authenticated
  // entry. Subsequent route changes within the session don't re-trigger it.
  private kycModalShown: boolean = false;
  private kycModalSub?: Subscription;
  private authSessionSub?: Subscription;
  private kycDialogRef?: MatDialogRef<ProfileSetupPromptComponent>;
  isKycDecisionLoading: boolean = false;

  constructor(
    private router: Router,
    public translate: TranslateService,
    public themeService: ThemeService,
    private layout: LayoutService,
    private cdr: ChangeDetectorRef,
    private jwtAuth: JwtAuthService,
    private cookieService: CookieService,
    private kycStatus: KycStatusService,
    private authSession: AuthSessionStateService,
    private dialog: MatDialog,
  ) {
    // Check Auth Token is valid
    this.jwtAuth.checkTokenIsValid().subscribe();

    // Close sidenav after route change in mobile
    this.routerEventSub = router.events
      .pipe(filter((event) :  event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((routeChange: NavigationEnd) => {
        this.layout.adjustLayout({ route: routeChange.url });
        this.scrollToTop();
        const currentUrl = (routeChange.urlAfterRedirects || routeChange.url) || '';
        try {
          const tree = this.router.parseUrl(currentUrl);
          const segments = tree.root.children['primary']?.segments.map(s => s.path) || [];
          const isTokenLogin = (segments.length === 2 && segments[0] === 'operations' && segments[1] === 'token-login')
            || (segments.length === 1 && segments[0] === 'tokenLogin');
          const isPoTokenDetails = segments.length === 3 && segments[0] === 'operations' && segments[1] === 'po-token' && segments[2] === 'po-details';
          const isRfqTokenFlow = segments[0] === 'rfqToken';
          this.hideChrome = isTokenLogin || isPoTokenDetails || isRfqTokenFlow;
        } catch {
          this.hideChrome = false;
        }
        this.suppressViewLoader = this.shouldSuppressViewLoader(currentUrl);
        // Ensure layout width matches chrome visibility; preserve compact if user toggled it
        if (this.hideChrome) {
          this.layout.publishLayoutChange({ sidebarStyle: 'closed' });
        } else if (!this.layout.layoutConf.sidebarCompactToggle) {
          this.layout.publishLayoutChange({ sidebarStyle: 'full' });
        }
      });

    // Translator init
    const browserLang: any = translate.getBrowserLang();
    translate.use(browserLang.match(/en|fr/) ? browserLang : "en");


    // Check if sidebar should be collapsed
    // const collapsedRoutes = ['/search/products', '/supplier', '/search/impa/impaList'];
    // const routerEvent = router.events
    // .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
    // .subscribe((routeChange: NavigationEnd) => {
    //   this.shouldCollapseSidebar = collapsedRoutes.some(route => routeChange.url.startsWith(route));
    //   this.layout.publishLayoutChange({
    //     sidebarStyle: this.shouldCollapseSidebar ? 'compact' : 'full',
    //   });
    //   this.scrollToTop();
    // });


  }

  ngOnInit() {
    console.log('loading admin layout')
    this.authSession.syncFromCookies();
    this.authSessionSub = this.authSession.state$.subscribe((state) => {
      if (state === 'EXPIRED') {
        this.isKycDecisionLoading = false;
        this.kycDialogRef?.close();
        this.kycDialogRef = undefined;
        this.kycStatus.reset();
      }
    });
    // Prime KYC status and, once it resolves, show the verification modal if
    // needed. Hosting this here (rather than inside any single dashboard)
    // ensures the modal fires on whichever dashboard route the user actually
    // lands on after login — supplier, buyer, or anywhere else.
    // Decide the modal from the shared status stream. Only a successful KYC
    // response is authoritative; authentication and API errors simply stop
    // the loader and never become a NOT_STARTED modal decision.
    this.isKycDecisionLoading = this.shouldWaitForKycDecision();
    this.kycStatus.refresh().catch(() => {
      this.isKycDecisionLoading = false;
    });
    this.kycModalSub = this.kycStatus.status$.subscribe((snap) => {
      if (snap.loadState === 'ERROR') {
        this.isKycDecisionLoading = false;
        return;
      }
      if (snap.loadState === 'SUCCESS' && snap.fetchedAt > 0) {
        this.isKycDecisionLoading = false;
        this.maybeOpenKycModal(snap);
      }
    });
    this.isWarningNotification = this.cookieService.get("isWarningNotification") ? JSON.parse(this.cookieService.get("isWarningNotification")) : false;
    this.isForceNotification = this.cookieService.get("isForceNotification") ? JSON.parse(this.cookieService.get("isForceNotification")) : false;
    this.notificationDays = this.cookieService.get("notificationDays") ? Number(this.cookieService.get("notificationDays")) : 0;
    this.layoutConfSub = this.layout.layoutConf$.subscribe((layoutConf) => {
      this.layoutConf = layoutConf;
      this.adminContainerClasses = this.updateAdminContainerClasses(
        this.layoutConf
      );
      this.cdr.markForCheck();
    });

    // FOR MODULE LOADER FLAG
    this.moduleLoaderSub = this.router.events.subscribe((event) => {
      if (
        event instanceof RouteConfigLoadStart ||
        event instanceof ResolveStart
      ) {
        this.isModuleLoading = true;
      }
      if (event instanceof RouteConfigLoadEnd || event instanceof ResolveEnd) {
        this.isModuleLoading = false;
      }
    });

    // Initialize chrome visibility and loader suppression for direct loads
    const initialUrl = this.router.url || '';
    try {
      const tree = this.router.parseUrl(initialUrl);
      const segments = tree.root.children['primary']?.segments.map(s => s.path) || [];
      const isTokenLogin = (segments.length === 2 && segments[0] === 'operations' && segments[1] === 'token-login')
        || (segments.length === 1 && segments[0] === 'tokenLogin');
      const isPoTokenDetails = segments.length === 3 && segments[0] === 'operations' && segments[1] === 'po-token' && segments[2] === 'po-details';
      const isRfqTokenFlow = segments[0] === 'rfqToken';
      this.hideChrome = isTokenLogin || isPoTokenDetails || isRfqTokenFlow;
    } catch {
      this.hideChrome = false;
    }
    this.suppressViewLoader = this.shouldSuppressViewLoader(initialUrl);
    if (this.hideChrome) {
      this.layout.publishLayoutChange({ sidebarStyle: 'closed' });
    }
  }
  private shouldSuppressViewLoader(url: string): boolean {
    try {
      const tree = this.router.parseUrl(url || '');
      const segments = tree.root.children['primary']?.segments.map(s => s.path) || [];
      const primaryPath = segments.join('/');
      return (
        primaryPath.startsWith('buyer-data-management/my-supplier') ||
        primaryPath.startsWith('buyer-data-management/my-suppliers')
      );
    } catch {
      return false;
    }
  }
  @HostListener("window:resize", ["$event"])
  onResize(event:any) {
    if (this.hideChrome) {
      // Keep sidebar closed on token-login during resizes (e.g., DevTools open)
      this.layout.publishLayoutChange({
        isMobile: this.layout.isSm(),
        sidebarStyle: 'closed',
      });
    } else {
      this.layout.adjustLayout(event);
    }
  }

  scrollToTop() {
    if (document) {
      setTimeout(() => {
        let element;
        if (this.layoutConf.topbarFixed) {
          element = <HTMLElement>(
            document.querySelector("#rightside-content-hold")
          );
        } else {
          element = <HTMLElement>document.querySelector("#main-content-wrap");
        }
        element.scrollTop = 0;
      });
    }
  }
  ngOnDestroy() {
    if (this.moduleLoaderSub) {
      this.moduleLoaderSub.unsubscribe();
    }
    if (this.layoutConfSub) {
      this.layoutConfSub.unsubscribe();
    }
    if (this.routerEventSub) {
      this.routerEventSub.unsubscribe();
    }
    if (this.kycModalSub) {
      this.kycModalSub.unsubscribe();
    }
    if (this.authSessionSub) {
      this.authSessionSub.unsubscribe();
    }
  }
  closeSidebar() {
    this.layout.publishLayoutChange({
      sidebarStyle: "closed",
    });
  }

  private maybeOpenKycModal(snap: KycStatusSnapshot) {
    console.log('[KYC-Modal] evaluating snapshot →', {
      status: snap.status,
      finalDecision: snap.finalDecision,
      completionPercent: snap.completionPercent,
      kycId: snap.kycId,
      kycReferenceNumber: snap.kycReferenceNumber,
      fetchedAt: snap.fetchedAt,
      kycModalShown: this.kycModalShown,
      hideChrome: this.hideChrome,
      isApplicantRole: this.kycStatus.isApplicantRole(),
      loadState: snap.loadState,
      sessionActive: this.authSession.isActive,
    });

    if (!this.authSession.isActive || snap.loadState !== 'SUCCESS') {
      console.log('[KYC-Modal] suppressed — session inactive or KYC request unsuccessful');
      return;
    }

    if (this.kycModalShown) {
      console.log('[KYC-Modal] suppressed — already shown this session');
      return;
    }
    if (this.hideChrome) {
      console.log('[KYC-Modal] suppressed — hideChrome (token route)');
      return;
    }
    if (!this.kycStatus.isApplicantRole()) {
      console.log('[KYC-Modal] suppressed — non-applicant role (admin/other)');
      return;
    }
    if ((snap.finalDecision === 'APPROVED' && snap.completionPercent === 100) || snap.exempt) {
      console.log('[KYC-Modal] suppressed — APPROVED or exempt');
      return;
    }

    // Fire for every non-APPROVED state. The modal copy adapts per status:
    //   NOT_STARTED / IN_PROGRESS         → action CTA (Start / Resume)
    //   SUBMITTED / UNDER_REVIEW          → informational (Got it)
    //   CLARIFICATION_REQUIRED / REJECTED → action CTA (Open / Resubmit)
    this.kycModalShown = true;
    console.log('[KYC-Modal] OPENING — status:', snap.status, 'ref:', snap.kycReferenceNumber);

    const ref = this.dialog.open(ProfileSetupPromptComponent, {
      disableClose: true,
      panelClass: 'profile-setup-dialog',
      data: {
        mode: 'KYC',
        kycReferenceNumber: snap.kycReferenceNumber,
        kycStatus: snap.status,
      },
    });
    this.kycDialogRef = ref;

    ref.afterClosed().subscribe((result) => {
      this.kycDialogRef = undefined;
      if (result === 'proceed') {
        this.router.navigateByUrl('/settings/kyc');
      }
      // 'later' / 'Got it' is a no-op — sidebar gating keeps the user on track.
    });
  }

  private shouldWaitForKycDecision(): boolean {
    return this.authSession.isActive && !this.hideChrome && this.kycStatus.isApplicantRole()
      && this.kycStatus.snapshot.loadState !== 'SUCCESS';
  }

  // Hover-expand disabled: collapsed sidebar stays collapsed until user clicks toggle
  sidebarMouseenter(e:any) {}
  sidebarMouseleave(e:any) {}

  updateAdminContainerClasses(layoutConf: ILayoutConf): AdminContainerClasses {
    return {
      "navigation-top": layoutConf.navigationPos === "top",
      "sidebar-full": layoutConf.sidebarStyle === "full",
      "sidebar-compact":
        layoutConf.sidebarStyle === "compact",
      "compact-toggle-active": layoutConf.sidebarCompactToggle || false,
      "sidebar-compact-big":
        layoutConf.sidebarStyle === "compact-big" &&
        layoutConf.navigationPos === "top",
      "sidebar-opened":
        layoutConf.sidebarStyle !== "closed" &&
        layoutConf.navigationPos === "top",
      "sidebar-closed": layoutConf.sidebarStyle === "closed",
      "fixed-topbar":
        (layoutConf.topbarFixed && layoutConf.navigationPos === "top") || false,
    };
  }
  closeNotification() {
    this.isWarningNotification = false;
    this.isForceNotification = false;
  }
  navigateToURL() {
    this.isWarningNotification = false;
    this.isForceNotification = false;
    this.router.navigateByUrl('/dashboard/user-profile', {
      state: {
        changepassword: true
      },
    });
  }

  closeDashboardSideNav() {
    this.router.navigateByUrl(this.dashboardSideNavReturnUrl);
  }

  getDashboardSideNavBackLink(){
    if(this.router.getCurrentNavigation()){
      const state = this.router.getCurrentNavigation()?.extras?.state;
    // Ensure token-login is treated as full-width in layout service
    if (!this.layout.fullWidthRoutes.includes('operations/token-login')) {
      this.layout.fullWidthRoutes.push('operations/token-login');
    }
      if(state && state['returnUrl']){
        this.dashboardSideNavReturnUrl = state['returnUrl'];
      } else {
        this.dashboardSideNavReturnUrl = "/dashboard/admin";
      }
    }
  }
}
